import { toast } from "sonner";

/**
 * Gestione centralizzata errori.
 *
 * Ogni toast di errore deve spiegare:
 *  1. COSA l'utente stava facendo  → contextMessage
 *  2. PERCHÉ è fallito             → dettaglio dal backend / status HTTP
 *
 * @param {Error|Object} error           – l'oggetto errore (axios o generico)
 * @param {string}       contextMessage  – Azione che è fallita (es. "Salvataggio fattura non riuscito")
 */
export function handleApiError(error, contextMessage = "") {
  // ---- 1. Estrai il dettaglio dal backend ----
  let backendDetail = "";
  const status = error?.response?.status;

  if (error?.response?.data?.detail) {
    const d = error.response.data.detail;
    if (typeof d === "string") {
      backendDetail = d;
    } else if (Array.isArray(d)) {
      backendDetail = d.map(e => e.msg || e.message).filter(Boolean).join("; ");
    } else if (d.message) {
      backendDetail = d.message;
    }
  } else if (error?.response?.data?.message) {
    backendDetail = error.response.data.message;
  } else if (error?.message) {
    // Errori di rete / timeout
    if (error.message.includes("Network Error") || error.message.includes("ERR_NETWORK")) {
      backendDetail = "Impossibile raggiungere il server. Verifica la tua connessione.";
    } else if (error.message.includes("timeout")) {
      backendDetail = "Il server non ha risposto in tempo. Riprova tra qualche istante.";
    } else if (!error.response) {
      // Error generico JS (validation, etc.) – usa il suo messaggio
      backendDetail = error.message;
    }
  }

  // ---- 2. Hint basato sullo status HTTP (solo come fallback) ----
  let statusHint = "";
  if (status === 401) statusHint = "Sessione scaduta — effettua nuovamente il login";
  else if (status === 403) statusHint = "Non hai i permessi per questa operazione";
  else if (status === 404) statusHint = "La risorsa richiesta non esiste o è stata rimossa";
  else if (status === 409) statusHint = "Conflitto: un elemento con questi dati esiste già";
  else if (status === 413) statusHint = "Il file caricato è troppo grande";
  else if (status === 422) statusHint = "Dati non validi";
  else if (status === 429) statusHint = "Troppe richieste — attendi qualche secondo e riprova";
  else if (status >= 500) statusHint = "Errore interno del server";

  // ---- 3. Componi il messaggio finale ----
  //  Priorità: backendDetail > statusHint > fallback generico
  let detail = backendDetail || statusHint || "";
  let finalMessage;

  if (contextMessage && detail) {
    // "Salvataggio fattura non riuscito: il fornitore non esiste"
    finalMessage = `${contextMessage}: ${detail}`;
  } else if (contextMessage) {
    finalMessage = contextMessage;
  } else if (detail) {
    finalMessage = detail;
  } else {
    finalMessage = "Si è verificato un errore imprevisto. Riprova.";
  }

  toast.error(finalMessage);

  // Log in console (solo dev)
  if (process.env.NODE_ENV !== "production") {
    console.group("API Error");
    console.log("Status:", status);
    console.log("Context:", contextMessage);
    console.log("Backend detail:", backendDetail);
    console.log("Final message:", finalMessage);
    console.groupEnd();
  }

  return { message: finalMessage, status };
}

/**
 * Wrapper per chiamate API con gestione errori integrata.
 */
export async function safeApiCall(apiCall, errorMessage) {
  try {
    const response = await apiCall();
    return { data: response.data, error: null };
  } catch (error) {
    handleApiError(error, errorMessage);
    return { data: null, error };
  }
}

/**
 * Toast successo.
 */
export function showSuccess(message, options = {}) {
  toast.success(message, { duration: 3000, ...options });
}

/**
 * Toast conferma azione.
 */
export function showActionConfirm(message, onConfirm, onCancel) {
  toast(message, {
    action: { label: "Conferma", onClick: onConfirm },
    cancel: { label: "Annulla", onClick: onCancel },
    duration: 10000,
  });
}

export default { handleApiError, safeApiCall, showSuccess, showActionConfirm };
